import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useIdHash } from '../../../hooks';
import { IGameConfig, IGameRules } from '../../../Models/GameSetupModels';
import {
  IAdditionalGameProperties,
  IVisibleLobby,
} from '../../../Models/LobbyModels';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { playerCountText } from '../LobbyListItem/LobbyListItem';
import StartScreen from '../StartScreen/StartScreen';
import {
  BUTTON_TEXT_CLASS_NAME,
  PRIMARY_BUTTON_CLASS_NAME,
  SECONDARY_BUTTON_CLASS_NAME,
} from '../StartScreen/styles';
import LobbyPlayers from './LobbyPlayers';
import LobbyRules from './LobbyRules';
import { RULESETS, rulesetName } from './rulesets';

interface ILobbyProps {
  lobby: IVisibleLobby;
  userId: string;
  leaveLobby: () => void;
  isLobbyAdmin: boolean;
  startGame: (gameConfig: IGameConfig) => void;
  previousConfig: IGameConfig;
  updateLobby: (
    name: string,
    additionalGameProperties: IAdditionalGameProperties
  ) => Promise<void>;
  replaceBotInGame: () => void;
}

interface ILobbySettings {
  numberOfBots: number;
  gameRules: IGameRules;
}

const STARTING_TIMEOUT_MS = 5000;

function lobbyStatus(
  lobby: IVisibleLobby,
  isLobbyAdmin: boolean,
  numberOfBots: number,
  canJoinRunningGame: boolean
) {
  const playerCount = playerCountText(lobby.players.length, numberOfBots);
  if (canJoinRunningGame) {
    return { title: 'Game in progress', hint: 'Take over from a bot' };
  }
  if (isLobbyAdmin) {
    return lobby.players.length + numberOfBots > 1
      ? { title: 'Ready to start', hint: playerCount }
      : {
          title: 'Waiting for players',
          hint: 'Add a bot, or wait for someone to join',
        };
  }
  if (lobby.additionalGameProperties.currentGameId) {
    return { title: 'Game in progress', hint: "You'll join the next one" };
  }
  return { title: 'Waiting for the host', hint: playerCount };
}

export default function Lobby({
  lobby,
  userId,
  leaveLobby,
  isLobbyAdmin,
  startGame,
  previousConfig,
  updateLobby,
  replaceBotInGame,
}: ILobbyProps) {
  const [settings, setSettings] = useState<ILobbySettings>(() => ({
    numberOfBots: lobby.additionalGameProperties.numberOfBots,
    gameRules: previousConfig.gameRules,
  }));
  const [chosenRuleset, setChosenRuleset] = useState(() =>
    rulesetName(previousConfig.gameRules)
  );
  const [isStarting, setIsStarting] = useState(false);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const idHash = useIdHash(userId);

  useEffect(() => {
    if (!isStarting) {
      return;
    }
    const timeout = setTimeout(() => setIsStarting(false), STARTING_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isStarting]);

  const { additionalGameProperties } = lobby;
  const shown: ILobbySettings = isLobbyAdmin
    ? settings
    : {
        numberOfBots: additionalGameProperties.numberOfBots,
        gameRules:
          additionalGameProperties.gameRules ?? previousConfig.gameRules,
      };
  const ruleset = isLobbyAdmin ? chosenRuleset : rulesetName(shown.gameRules);
  const canStart = lobby.players.length + shown.numberOfBots > 1;
  const canJoinRunningGame =
    !!additionalGameProperties.currentGameId &&
    additionalGameProperties.numberOfBots > 0;
  const status = lobbyStatus(
    lobby,
    isLobbyAdmin,
    shown.numberOfBots,
    canJoinRunningGame
  );

  const applySettings = (next: ILobbySettings) => {
    setSettings(next);
    updateLobby(lobby.name, { ...additionalGameProperties, ...next });
  };

  const changeRuleset = (name: string) => {
    setChosenRuleset(name);
    const preset = RULESETS.find((r) => r.name === name);
    if (preset) {
      applySettings({ ...settings, gameRules: preset.rules });
    }
  };

  const changeRules = (gameRules: IGameRules) => {
    setChosenRuleset(rulesetName(gameRules));
    applySettings({ ...settings, gameRules });
  };

  const start = () => {
    if (isStarting || !canStart) {
      return;
    }
    setIsStarting(true);
    startGame({
      numberOfPlayers: lobby.players.length,
      numberOfBots: settings.numberOfBots,
      gameRules: settings.gameRules,
    });
  };

  const joinRunningGame = () => {
    if (isStarting) {
      return;
    }
    setIsStarting(true);
    replaceBotInGame();
  };

  const leave = () => {
    if (isLobbyAdmin) {
      setIsLeaveDialogOpen(true);
    } else {
      leaveLobby();
    }
  };

  const footer = (
    <>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-px pl-[10px]">
        <span className="max-w-full truncate text-[16px] font-bold">
          {status.title}
        </span>
        <span className="max-w-full truncate text-[12px] font-medium text-ink-muted">
          {status.hint}
        </span>
      </div>
      {canJoinRunningGame && (
        <button
          onClick={joinRunningGame}
          disabled={isStarting}
          className={
            isLobbyAdmin
              ? SECONDARY_BUTTON_CLASS_NAME
              : PRIMARY_BUTTON_CLASS_NAME
          }
        >
          <span className={BUTTON_TEXT_CLASS_NAME}>Join</span>
        </button>
      )}
      {isLobbyAdmin && (
        <button
          onClick={start}
          disabled={isStarting || !canStart}
          className={classNames(PRIMARY_BUTTON_CLASS_NAME, 'min-w-[120px]')}
        >
          <span className={BUTTON_TEXT_CLASS_NAME}>
            {isStarting ? 'Starting…' : 'Start game'}
          </span>
        </button>
      )}
    </>
  );

  return (
    <StartScreen
      title={lobby.name}
      backLabel="Leave lobby"
      onBack={leave}
      footer={footer}
    >
      <LobbyPlayers
        players={lobby.players}
        idHash={idHash}
        numberOfBots={shown.numberOfBots}
        onBotsChange={
          isLobbyAdmin
            ? (numberOfBots) => applySettings({ ...settings, numberOfBots })
            : undefined
        }
      />
      <LobbyRules
        rules={shown.gameRules}
        ruleset={ruleset}
        onRulesetChange={isLobbyAdmin ? changeRuleset : undefined}
        onRulesChange={isLobbyAdmin ? changeRules : undefined}
      />
      {isLeaveDialogOpen && (
        <ConfirmDialog
          title="Leave this lobby?"
          message="You're the host, so nobody can start a game here once you leave."
          confirmLabel="Leave"
          cancelLabel="Stay"
          onConfirm={leaveLobby}
          onCancel={() => setIsLeaveDialogOpen(false)}
        />
      )}
    </StartScreen>
  );
}
